// Type-safe API client. All methods return Promise<T> and throw ApiError on
// non-2xx responses. 401 auto-redirects to /login.

import type {
  GuardrailSnapshot,
  KillSwitchToggleReq,
  PlansList,
  PositionsResponse,
  SettingsPutBody,
  SettingsResponse,
  TradingPlan,
} from './types'

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message)
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  })
  if (res.status === 401) {
    if (!location.pathname.startsWith('/login')) {
      window.location.href = '/login'
    }
    throw new ApiError(401, 'unauthorized')
  }
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new ApiError(res.status, (body as { error?: string } | null)?.error ?? res.statusText, body)
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

// ─── Types ────────────────────────────────────────────────────────────────

export interface User {
  id: number
  email: string
  name: string
}

export interface Stats {
  users: number
  active_sessions: number
}

export interface VersionInfo {
  commit: string
  built_at: string
}

// ─── API ──────────────────────────────────────────────────────────────────

export const api = {
  // Auth
  login: (email: string, password: string) =>
    request<User>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
  me: () => request<User>('/auth/me'),

  // Meta
  health: () => request<{ ok: boolean }>('/api/health'),
  version: () => request<VersionInfo>('/api/version'),
  stats: () => request<Stats>('/api/stats'),

  // Admin (QT U10) — every method is session-cookie gated server-side.
  plan: (date?: string) =>
    request<TradingPlan>(`/api/admin/plan${date ? `?date=${encodeURIComponent(date)}` : ''}`),
  plansList: (limit = 20) => request<PlansList>(`/api/admin/plans?limit=${limit}`),
  // Single plan by KST date. 404 when no row exists — callers that
  // want "today or next available" should swallow the 404.
  planForDate: (date: string) =>
    request<TradingPlan>(`/api/admin/plan?date=${encodeURIComponent(date)}`),
  positions: () => request<PositionsResponse>('/api/admin/positions'),
  guardrail: () => request<GuardrailSnapshot>('/api/admin/guardrail'),
  killSwitch: (body: KillSwitchToggleReq) =>
    request<GuardrailSnapshot['kill_switch']>('/api/admin/guardrail/kill-switch', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  settings: () => request<SettingsResponse>('/api/admin/settings'),
  settingsSave: (body: SettingsPutBody) =>
    request<SettingsResponse>('/api/admin/settings', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  // Job-runs API moved to admin-design/_common/jobs (used directly by
  // JobsPage). The path also moved from /api/admin/job-runs/* to
  // /api/admin/jobs/runs/* to align with the canonical mount point.

  // Dashboard (phase 1) — balance_state + balance_events for the active mode.
  dashboardBalance: (events = 120) =>
    request<DashboardBalance>(`/api/admin/dashboard/balance?events=${events}`),

  // Dashboard (phase 2) — merged trend + KIS news bucketed by holdings.
  // Server-side pagination + optional window. Pass hours=0 to drop the
  // lower bound entirely (the /news paginator uses this so the operator
  // can page back through the full retention window, not just 24h).
  dashboardNews: (
    opts: { limit?: number; hours?: number; offset?: number } = {},
  ) => {
    const limit = opts.limit ?? 40
    const hours = opts.hours ?? 6
    const offset = opts.offset ?? 0
    return request<DashboardNews>(
      `/api/admin/dashboard/news?limit=${limit}&hours=${hours}&offset=${offset}`,
    )
  },

  // Dashboard (phase 5) — Bithumb read-only account snapshot.
  dashboardBithumb: () =>
    request<DashboardBithumb>('/api/admin/dashboard/bithumb'),
  dashboardIndices: () =>
    request<DashboardIndices>('/api/admin/dashboard/indices'),

  // Intraday KRX snapshots — LLM-driven regime + watchlist updates
  // every 30 min during trading hours. Cross-check against the nightly
  // plan on the /plan page. `raw=1` asks the server to include the
  // raw LLM response body for audit.
  dashboardIntraday: (limit = 20, raw = false) => {
    const q = new URLSearchParams({ limit: String(limit) })
    if (raw) q.set('raw', '1')
    return request<IntradayPayload>(`/api/admin/dashboard/intraday?${q.toString()}`)
  },

  // Market-wide news aggregate. scope=market feeds the KIS 흐름 card;
  // scope=crypto feeds the 암호화폐 흐름 card under the Bithumb section.
  dashboardMarketOverview: (
    hours = 24,
    limit = 15,
    scope: 'market' | 'crypto' | 'ticker' = 'market',
  ) =>
    request<MarketOverview>(
      `/api/admin/dashboard/market-overview?hours=${hours}&limit=${limit}&scope=${scope}`,
    ),

  // Watchlist — persisted monitoring list shared with the suggestion
  // accept-hook.
  watchlistList: () =>
    request<{ items: WatchlistEntry[] }>('/api/admin/dashboard/watchlist'),
  watchlistAdd: (ticker: string, label?: string) =>
    request<{ items: WatchlistEntry[] }>('/api/admin/dashboard/watchlist', {
      method: 'POST',
      body: JSON.stringify({ ticker, label: label ?? '' }),
      headers: { 'Content-Type': 'application/json' },
    }),
  watchlistRemove: (ticker: string) =>
    request<{ items: WatchlistEntry[] }>(
      `/api/admin/dashboard/watchlist/${encodeURIComponent(ticker)}`,
      { method: 'DELETE' },
    ),
  // Backfill 한글 종목명 for legacy rows that landed before the
  // auto-resolve flow. Sequential KIS calls — slow on large lists
  // but cheap (one chunk per session) and operator-triggered only.
  watchlistResolveNames: () =>
    request<{ items: WatchlistEntry[]; updated: number }>(
      '/api/admin/dashboard/watchlist/resolve-names',
      { method: 'POST' },
    ),

  // Bithumb orders (stage B). Manual placement + cancel + history.
  bithumbOrderList: (params?: { symbol?: string; state?: string; limit?: number }) => {
    const q = new URLSearchParams()
    if (params?.symbol) q.set('symbol', params.symbol)
    if (params?.state) q.set('state', params.state)
    if (params?.limit) q.set('limit', String(params.limit))
    const qs = q.toString()
    return request<{ items: BithumbOrder[] }>(
      `/api/admin/bithumb-orders${qs ? `?${qs}` : ''}`,
    )
  },
  bithumbOrderPlace: (body: BithumbOrderPlaceRequest) =>
    request<BithumbOrder>('/api/admin/bithumb-orders', {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'Content-Type': 'application/json' },
    }),
  bithumbOrderCancel: (uuid: string) =>
    request<BithumbOrder>(`/api/admin/bithumb-orders/${encodeURIComponent(uuid)}`, {
      method: 'DELETE',
    }),
  // Current guardrail config + today's used KRW. Rendered on the
  // order form so the operator sees "8.3M / 10M KRW used today" +
  // kill-switch state inline.
  bithumbRiskStatus: () =>
    request<BithumbRiskStatus>('/api/admin/bithumb-orders/risk-status'),

  // Crypto bot trading journal — round-trip cycles + open positions
  // + today-KST realised PnL roll-up. Mode follows bithumb.active_mode.
  cryptoJournal: () => request<CryptoJournal>('/api/admin/crypto-journal'),
  kisJournal: () => request<CryptoJournal>('/api/admin/kis-journal'),

  // Crypto 24/7 shadow-session verdicts journal.
  cryptoVerdicts: (symbol?: string, limit = 50) => {
    const q = new URLSearchParams()
    if (symbol) q.set('symbol', symbol)
    if (limit) q.set('limit', String(limit))
    return request<{ items: CryptoVerdict[] }>(
      `/api/admin/dashboard/crypto-verdicts${q.toString() ? `?${q.toString()}` : ''}`,
    )
  },

  // News worker — queue snapshot (pending vs done counts, per-service
  // breakdown, suspected duplicate groups, in-flight worker state).
  newsQueue: () =>
    request<NewsQueueResponse>('/api/admin/dashboard/news/queue'),

  // News worker — fire one immediate tick, bypassing the cron interval.
  // Returns immediately with a started_at timestamp; the actual work
  // runs on a detached server-side ctx so an HTTP disconnect doesn't
  // kill it.
  newsRunNow: () =>
    request<{ started_at: number; status: string }>(
      '/api/admin/dashboard/news/run-now',
      { method: 'POST' },
    ),

  // News dedup — collapse duplicate-title rows to one (per group), keep
  // the row with a summary if any, else newest captured. Returns audit
  // counts so the UI can banner "5 그룹에서 7건 삭제".
  newsDedup: () =>
    request<NewsDedupResponse>(
      '/api/admin/dashboard/news/dedup',
      { method: 'POST' },
    ),

  // News reset-pending — zero summary_attempts + clear last_error on all
  // rows still waiting for a summary. Kicks stuck queue items (maxed out
  // attempts after an LLM outage) back into the worker's next tick.
  newsResetPending: () =>
    request<{ rows_reset: number }>(
      '/api/admin/dashboard/news/reset-pending',
      { method: 'POST' },
    ),

  // Calendar — monthly per-day aggregate (plans + news + trades). One
  // fetch per month view; cells below-expand reuses existing plan /
  // news components so this endpoint stays summary-only.
  dashboardCalendar: (month: string) =>
    request<CalendarPayload>(
      `/api/admin/dashboard/calendar?month=${encodeURIComponent(month)}`,
    ),
  // Drill-through: news + crypto orders + KIS orders for one KST date.
  // Cell click triggers this; keeps the month summary fetch cheap.
  dashboardCalendarDay: (date: string) =>
    request<CalendarDayDetailPayload>(
      `/api/admin/dashboard/calendar/day?date=${encodeURIComponent(date)}`,
    ),

  // Dashboard (phase 3) — 거래일지 (trading journal) for a given KST date.
  dashboardRetrospective: (date?: string) =>
    request<DashboardRetrospective>(
      `/api/admin/dashboard/retrospective${date ? `?date=${encodeURIComponent(date)}` : ''}`,
    ),

  // Dashboard (phase 4) — next open KST trading day's plan.
  dashboardNextDayPlan: () =>
    request<TradingPlan>('/api/admin/dashboard/next-day-plan'),

  // Manual trigger — forces an immediate nightly run. Default (force=false)
  // preserves idempotency: skips when a healthy plan already exists.
  // force=true replaces even a healthy plan with a fresh regeneration;
  // used by the Plan page's 지금 실행 button after a confirm dialog.
  nightlyRunNow: (opts?: { force?: boolean }) =>
    request<{ ok: boolean; status: string; force: boolean }>(
      `/api/admin/nightly/run${opts?.force ? '?force=1' : ''}`,
      { method: 'POST' },
    ),

  // Manual trigger — fires one intraday analysis tick immediately. The
  // cron runs every 30 min during KRX hours; this lets the operator
  // poke it between ticks. force=true also bypasses the
  // IsKRXTradingHours gate so after-hours debugging works.
  intradayRunNow: (opts?: { force?: boolean }) =>
    request<{ ok: boolean; status: string; force: boolean }>(
      `/api/admin/dashboard/intraday/run${opts?.force ? '?force=1' : ''}`,
      { method: 'POST' },
    ),

  // KIS broker-state audit + manual resume. The market/trade split means
  // two scopes now exist (kis_market and kis_trade); the snapshot lists
  // both so the operator can see which one is paused, and resume unblocks
  // the named scope without touching the other.
  kisBrokerState: () =>
    request<{ items: Array<{ name: string; paused: boolean; reason?: string; paused_at?: number; consecutive_failures: number }> }>(
      '/api/admin/kis/broker-state',
    ),
  kisBrokerResume: (name: 'kis_market' | 'kis_trade' | 'kis') =>
    request<{ name: string; rows_updated: number }>(
      `/api/admin/kis/broker-state/resume?name=${encodeURIComponent(name)}`,
      { method: 'POST' },
    ),

  // Plan name-backfill — walks the watchlist of the plan for `date`
  // (default today KST) and fills 한글 종목명 via KIS search-stock-info
  // without re-running the LLM. Recovery path when a plan was generated
  // before NameResolver wiring or KIS credentials landed.
  //
  // Response audit: filled = rows UPSERTed; attempted = tickers whose
  // Name was empty before the call; errors = per-ticker KIS error
  // message (circuit open, rate limit, etc.). attempted - filled -
  // len(errors) = tickers KIS resolved to an empty name (delisted /
  // unknown codes).
  planResolveNames: (date?: string) =>
    request<{
      for_date: string
      filled: number
      attempted: number
      errors?: Record<string, string>
    }>(
      `/api/admin/plans/resolve-names${
        date ? `?date=${encodeURIComponent(date)}` : ''
      }`,
      { method: 'POST' },
    ),

  // Live KIS quote for a 6-digit KRX ticker. Confirms brokerage
  // reachability during trading hours + drives the Dashboard's
  // "watched ticker" card.
  quote: (ticker: string) =>
    request<QuoteSnapshot>(`/api/admin/quote?ticker=${encodeURIComponent(ticker)}`),

  // Suggestions — advisory LLM-generated stock picks with rationale.
  // Phase 4/B-3: market filter (KR/US/empty=both) + per-market generate.
  suggestions: (status = 'active', market?: 'KR' | 'US') => {
    const qs = new URLSearchParams()
    if (status) qs.set('status', status)
    if (market) qs.set('market', market)
    const q = qs.toString()
    return request<SuggestionsListResponse>(
      `/api/admin/dashboard/suggestions${q ? `?${q}` : ''}`,
    )
  },
  // market: 'KR' | 'US' | 'both'. Server treats unknown / missing as
  // 'both', subject to per-market kill switches in settings.
  suggestionsGenerate: (market?: 'KR' | 'US' | 'both') =>
    request<SuggestionsGenerateResponse>(
      '/api/admin/dashboard/suggestions/generate',
      {
        method: 'POST',
        body: market ? JSON.stringify({ market }) : undefined,
      },
    ),
  suggestionReview: (id: number, status: 'accepted' | 'rejected', reviewer = 'operator') =>
    request<{ ok: boolean }>(
      `/api/admin/dashboard/suggestions/${id}/review`,
      { method: 'POST', body: JSON.stringify({ status, reviewer }) },
    ),

  // Force a single-item summary. Used by the /news page's 재요약 / 바로
  // 요약 buttons. Response is the ItemSummary; server also upserts it
  // into the latest news_summaries row so the next full poll sees it.
  summarizeNewsItem: (item: {
    id: string
    service: string
    title: string
    body?: string
    url?: string
    ticker?: string
    origin?: string
    published_at?: number
  }) =>
    request<NewsSummaryItem>('/api/admin/dashboard/news/summarize-item', {
      method: 'POST',
      body: JSON.stringify(item),
    }),

  // News clusters — embedding-based grouping over news_items. List
  // is filterable by scope + ticker + verdict (LLM-judge) + status
  // (active|archived|all); detail returns up to 50 most recent
  // member rows. cluster-actions returns the Phase 3 audit feed
  // (split + merge actions, most recent first).
  clustersList: (params?: {
    scope?: 'ticker' | 'market' | 'crypto'
    ticker?: string
    verdict?: 'keep' | 'split' | 'merge'
    status?: 'active' | 'archived' | 'all'
    limit?: number
  }) => {
    const q = new URLSearchParams()
    if (params?.scope) q.set('scope', params.scope)
    if (params?.ticker) q.set('ticker', params.ticker)
    if (params?.verdict) q.set('verdict', params.verdict)
    if (params?.status) q.set('status', params.status)
    if (params?.limit) q.set('limit', String(params.limit))
    const qs = q.toString()
    return request<ClustersListResponse>(
      `/api/admin/clusters${qs ? `?${qs}` : ''}`,
    )
  },
  clusterDetail: (id: number) =>
    request<ClusterDetailResponse>(`/api/admin/clusters/${id}`),
  clusterActions: (params?: {
    action?: 'split' | 'merge'
    limit?: number
  }) => {
    const q = new URLSearchParams()
    if (params?.action) q.set('action', params.action)
    if (params?.limit) q.set('limit', String(params.limit))
    const qs = q.toString()
    return request<ClusterActionsResponse>(
      `/api/admin/cluster-actions${qs ? `?${qs}` : ''}`,
    )
  },

  // US Equity master — daily-refreshed NASDAQ Trader symbol dump.
  // Three endpoints: paginated list/search, stats card, manual refresh.
  usEquityListings: (params?: {
    q?: string
    market?: string
    limit?: number
  }) => {
    const q = new URLSearchParams()
    if (params?.q) q.set('q', params.q)
    if (params?.market) q.set('market', params.market)
    if (params?.limit) q.set('limit', String(params.limit))
    const qs = q.toString()
    return request<USEquityListingsResponse>(
      `/api/admin/us-equity/listings${qs ? `?${qs}` : ''}`,
    )
  },
  usEquityStats: () =>
    request<USEquityStatsResponse>(`/api/admin/us-equity/stats`),
  usEquityRefresh: () =>
    request<{ started_at: number; status: string }>(
      `/api/admin/us-equity/refresh`,
      { method: 'POST' },
    ),

  // Overseas-stock snapshots (Phase 4 / B-2). Periodic per-ticker
  // KIS price + TA snapshot. Three endpoints mirror the us_equity
  // shape — read latest, read aggregate stats, fire manual snapshot.
  overseasSnapshotsLatest: (params?: { market?: string; limit?: number }) => {
    const q = new URLSearchParams()
    if (params?.market) q.set('market', params.market)
    if (params?.limit) q.set('limit', String(params.limit))
    const qs = q.toString()
    return request<OverseasSnapshotsResponse>(
      `/api/admin/overseas/snapshots/latest${qs ? `?${qs}` : ''}`,
    )
  },
  overseasStats: () =>
    request<OverseasStatsResponse>(`/api/admin/overseas/stats`),
  overseasSnapshot: () =>
    request<{ started_at: number; status: string }>(
      `/api/admin/overseas/snapshot`,
      { method: 'POST' },
    ),

  // Backtest (Phase 4 / B-4) — measures historical suggester hit-rate.
  // Stats: rolling aggregates by (period × market × action ×
  // sentiment_band × quality_verdict × window_days). Results: raw rows
  // for drilldown. Refresh: manual trigger of the daily cron's runner.
  backtestStats: (params?: {
    period_days?: number
    market?: string
    limit?: number
  }) => {
    const q = new URLSearchParams()
    if (params?.period_days) q.set('period_days', String(params.period_days))
    if (params?.market) q.set('market', params.market)
    if (params?.limit) q.set('limit', String(params.limit))
    const qs = q.toString()
    return request<BacktestStatsResponse>(
      `/api/admin/backtest/stats${qs ? `?${qs}` : ''}`,
    )
  },
  backtestResults: (params?: {
    market?: string
    action?: string
    window_days?: number
    limit?: number
  }) => {
    const q = new URLSearchParams()
    if (params?.market) q.set('market', params.market)
    if (params?.action) q.set('action', params.action)
    if (params?.window_days) q.set('window_days', String(params.window_days))
    if (params?.limit) q.set('limit', String(params.limit))
    const qs = q.toString()
    return request<BacktestResultsResponse>(
      `/api/admin/backtest/results${qs ? `?${qs}` : ''}`,
    )
  },
  backtestRefresh: () =>
    request<{ started_at: number; status: string }>(
      `/api/admin/backtest/refresh`,
      { method: 'POST' },
    ),
}

// News clustering wire types — mirror internal/httpapi/cluster_handlers.go.
export interface NewsCluster {
  id: number
  scope: 'ticker' | 'market' | 'crypto'
  ticker: string
  item_count: number
  label: string
  sentiment_avg?: number
  first_seen_at: number
  last_seen_at: number
  // Cluster-mode summary fields. NULL until the LLM has processed the
  // cluster; populated by the news worker's Phase 2.5 cluster pass and
  // fanned out to member rows in MarkClusterSummarized.
  summary_ko?: string
  sentiment?: string
  sentiment_score?: number
  summarized_at?: number
  // LLM-judge quality fields (Phase 2). NULL until the judge has
  // scored a cluster — backwards compatible with pre-Phase-2 rows.
  // Populated alongside the summary by the news worker's judge phase.
  quality_score?: number // 1..5 composite (coherence + sentiment_consistency)/2
  quality_verdict?: 'keep' | 'split' | 'merge' | ''
  quality_reason?: string
  quality_evaluated_at?: number
  // Phase 3 archive metadata (migration 0036). active is the default
  // status for every cluster ever created; archived means Phase 3
  // dissolved (split) or absorbed (merge) it. archived_into_id points
  // back at the survivor for merge cases — null for split.
  status?: 'active' | 'archived'
  archived_at?: number
  archive_reason?: string
  archived_into_id?: number
}

// ClusterAction is a row from the cluster_actions audit log. Phase 3
// writes one of these per executed split/merge so the admin UI can show
// "최근 자동 액션" without parsing the cluster row's archive_reason.
export interface ClusterAction {
  id: number
  action: 'split' | 'merge'
  primary_cluster_id: number
  secondary_cluster_id?: number
  member_count: number
  cosine_similarity?: number
  quality_score?: number
  quality_verdict?: string
  reason?: string
  created_at: number
}

export interface ClusterActionsResponse {
  actions: ClusterAction[]
}

export interface NewsClusterMember {
  id: string
  service: string
  origin?: string
  title: string
  url?: string
  ticker?: string
  sentiment?: string
  sentiment_score?: number
  published_at: number
  captured_at: number
}

export interface ClustersListResponse {
  clusters: NewsCluster[]
}

export interface ClusterDetailResponse {
  cluster: NewsCluster
  items: NewsClusterMember[]
}

// US Equity master wire types — mirror internal/httpapi/usequity_handlers.go.
export interface USEquityListing {
  symbol: string
  name: string
  market: string
  etf: boolean
  test_issue?: boolean
  financial_status?: string
  round_lot_size?: number
  last_refreshed_at: number
}

export interface USEquityListingsResponse {
  listings: USEquityListing[]
}

export interface USEquityMarketCount {
  market: string
  count: number
}

export interface USEquityStatsResponse {
  total: number
  last_refreshed_at?: number
  stale_hours_ago?: number
  breakdown: USEquityMarketCount[]
}

// Overseas-stock snapshot wire — mirrors internal/httpapi/overseas_handlers.go
// All derived/TA fields are optional because the snapshot worker writes
// SQL NULL when daily history is too short for the indicator (e.g. early
// ticks won't have SMA50 / RSI14 computable).
export interface OverseasSnapshot {
  ticker: string
  market: string
  captured_at: number
  last_price: number
  open_price: number
  high_price: number
  low_price: number
  prev_close: number
  volume: number
  change_pct_1d?: number
  change_pct_5d?: number
  change_pct_20d?: number
  volume_avg_20d?: number
  volume_ratio?: number
  rsi_14?: number
  sma_20?: number
  sma_50?: number
  kis_excd?: string
}

export interface OverseasSnapshotsResponse {
  snapshots: OverseasSnapshot[]
}

export interface OverseasStatsResponse {
  total_rows: number
  unique_tickers: number
  last_captured_at?: number
  oldest_captured_at?: number
  stale_hours_ago?: number
}

// Backtest wire types — mirror internal/httpapi/backtest_handlers.go.
// hit_rate / avg_return_pct / hit / entry_price / exit_price are all
// optional because backtest_results/stats keep them NULL when
// snapshot data is insufficient (no entry/exit price within ±48h).
export interface BacktestStatsRow {
  period_days: number
  market: string
  action: string
  sentiment_band: string
  quality_verdict: string
  window_days: number
  sample_size: number
  hit_count: number
  hit_rate?: number
  avg_return_pct?: number
  computed_at: number
}

export interface BacktestStatsResponse {
  stats: BacktestStatsRow[]
  period_days: number
  market: string
  total_results: number
}

export interface BacktestResultRow {
  id: number
  suggestion_id: number
  ticker: string
  market: string
  action: string
  window_days: number
  entry_price?: number
  exit_price?: number
  return_pct?: number
  hit?: number // 1=hit, 0=miss
  threshold_pct: number
  computed_at: number
}

export interface BacktestResultsResponse {
  results: BacktestResultRow[]
}

export interface DashboardBalancePosition {
  ticker: string
  qty: number
  avg_cost: number
  mkt_price: number
  mkt_value: number
}

export interface DashboardBalanceEvent {
  id: number
  trade_mode: string
  kind: 'fill' | 'mark' | 'eod_close' | 'manual'
  cash_krw: number
  equity_krw: number
  realised_pnl: number
  unrealised_pnl: number
  ref_id?: number | null
  note?: string
  created_at: string
}

export interface DashboardBalance {
  trade_mode: string
  cash_krw: number
  equity_krw: number
  realised_pnl_today: number
  unrealised_pnl: number
  positions: DashboardBalancePosition[]
  prev_close_equity: number
  delta_vs_prev_close: number
  delta_vs_prev_pct: number
  trading_date: string
  updated_at: number
  events: DashboardBalanceEvent[] | null
  /** Reason the broker call couldn't populate this row. Only set when
   *  the DB row is still empty AND the lazy-reconcile attempt failed
   *  (broker unavailable, GetBalance rejected, etc.). Null/undefined
   *  when the row has real data. */
  broker_error?: string
}

// ----- Bithumb dashboard ----------------------------------------------------
//
// All numeric fields ship as decimal strings — crypto quantities carry
// up to 8+ fractional digits and JavaScript number would lose precision
// on large ETH-style balances. The UI formats via Intl.NumberFormat on
// rendered display rather than performing arithmetic on the raw values.
export interface BithumbPosition {
  symbol: string
  currency: string
  quantity: string
  free: string
  locked: string
  avg_buy_price: string
  mark_price: string
  notional_krw: string
  unrealized_pl: string
  /** Bithumb daily close (KST 00:00 boundary). Empty string when
   *  pricing fell back to avg cost (ticker unavailable this tick). */
  prev_closing_krw?: string
  /** Server-computed daily change % = (mark - prev_close)/prev_close.
   *  Zero when prev_closing_krw is missing. */
  change_pct_24h?: number
}

export interface DashboardIndexItem {
  code: string
  label: string
  value: number
  at: number
  /** Prior trading-day close. Omitted by the server when KIS didn't
   *  return a usable prev-day diff this tick AND no persisted baseline
   *  exists; UI hides the "전일 대비" line in that case. */
  prev_close?: number
  /** Server-computed (value - prev_close). Signed. Omitted alongside
   *  prev_close when the baseline is unknown. */
  change?: number
  /** Server-computed change %. Signed. */
  change_pct?: number
  /** true when the value came from the persisted last-known snapshot
   *  (price_snapshots) instead of a live broker call this tick. UI
   *  shows a muted tone + "(마지막 N분 전)" tag in that case. */
  stale?: boolean
}

export interface DashboardIndices {
  items: DashboardIndexItem[]
  fetched_at: number
  error?: string
}

// Intraday KRX snapshot — one row per tick of the stock session
// cross-check analyser. Mirrors the nightly TradingPlan's shape so
// the UI can render both through the same renderer when comparing.
export interface IntradayTicker {
  symbol: string
  side: 'long' | 'short'
  thesis: string
  conviction: number
}

export interface IntradaySnapshot {
  id: number
  ticked_at: number
  regime: 'risk_on' | 'risk_off' | 'neutral' | 'degraded'
  score: number
  watchlist: IntradayTicker[]
  macro_summary: string
  risk_notes: string
  source_refs: string[]
  reason: string
  /** Raw LLM body — present only when the endpoint was called with
   *  ?raw=1. Used by the "raw details" inspector. */
  raw_llm_json?: unknown
}

export interface IntradayPayload {
  items: IntradaySnapshot[]
  fetched_at: number
}

export interface DashboardBithumb {
  configured: boolean
  cash_krw: string
  equity_krw: string
  positions: BithumbPosition[]
  fetched_at: number
  error?: string
}

// Bithumb orders. All money fields are decimal strings (same rationale
// as positions) so JS number arithmetic never touches them before render.
export type BithumbOrderSide = 'bid' | 'ask'
// 'limit' = limit order; 'price' = market buy (price field = total KRW
// to spend); 'market' = market sell (volume field = base asset qty).
export type BithumbOrderType = 'limit' | 'price' | 'market'
// State lifecycle: pending (our pre-POST row) → wait (open on exchange)
// → done / cancel / reject.
export type BithumbOrderState =
  | 'pending'
  | 'wait'
  | 'done'
  | 'cancel'
  | 'reject'

export interface BithumbOrder {
  id: number
  uuid: string
  client_oid: string
  provider: string
  symbol: string
  side: BithumbOrderSide
  ord_type: BithumbOrderType
  state: BithumbOrderState
  price: string
  volume: string
  executed_volume: string
  remaining_volume: string
  paid_fee: string
  locked: string
  submitted_by: string
  note?: string
  submitted_at: number
  updated_at: number
  closed_at?: number
  error_message?: string
}

export interface BithumbOrderPlaceRequest {
  symbol: string
  side: BithumbOrderSide
  ord_type: BithumbOrderType
  price?: string
  volume?: string
  note?: string
}

// Live view of the crypto.* risk-limit settings + today's cumulative
// buy-side KRW. Drives the order-form header: "kill switch ON",
// "8.3M / 10M today", "per-symbol max 20%".
export interface BithumbRiskStatus {
  account_equity_krw: string
  max_per_symbol_pct: string
  daily_krw_cap: string
  kill_switch: boolean
  today_used_krw: string
}

// Shadow-mode decision journal. One row per symbol per tick; score
// and conviction are floats in [-1, +1] so the UI can tone-code.
export interface CryptoVerdict {
  id: number
  provider: string
  symbol: string
  ticked_at: number
  verdict: 'buy' | 'sell' | 'hold' | 'skip'
  score: number
  conviction: number
  reason: string
  features?: CryptoVerdictFeature[]
  executed: boolean
}

export interface CryptoVerdictFeature {
  family: string
  name: string
  score: number
  reason: string
}

export interface SuggestionEvidence {
  kind: string
  id: string
  note?: string
}

export interface StockSuggestion {
  id: number
  ticker: string
  /** Market the suggestion applies to. 'KR' = KOSPI/KOSDAQ 6-digit;
   *  'US' = NASDAQ/NYSE/AMEX/BATS alphabetic. Legacy rows persisted
   *  before migration 0035 may carry an empty string — treated as KR
   *  by the dashboard's KR tab. */
  market?: 'KR' | 'US' | ''
  action: 'watch' | 'buy' | 'sell' | 'avoid'
  conviction: number
  rationale: string
  evidence: SuggestionEvidence[]
  status: 'active' | 'accepted' | 'rejected' | 'stale'
  model?: string
  created_at: string
  reviewed_at?: string
  reviewed_by?: string
}

// NewsQueueResponse is the worker queue snapshot. The dashboard panel
// uses it to show "N pending / N summarised, last summary 3min ago,
// worker idle" plus a duplicates-suspected list so the operator can
// confirm a KIS-side repost vs an internal storage dup.
export interface NewsQueueResponse {
  /** Legacy total: pending_retryable + pending_exhausted. Kept for
   *  wire-shape compatibility; new UI should prefer the split fields. */
  pending: number
  /** Rows still eligible for the worker (summary_attempts < 5). */
  pending_retryable: number
  /** Rows that hit MaxSummaryAttempts and will never retry unless
   *  reset via the 대기열 초기화 button. */
  pending_exhausted: number
  summarised: number
  by_source: Record<string, number>
  last_summary_at?: number
  oldest_pending_at?: number
  worker_inflight: boolean
  worker_started_at?: number
  last_failure?: string
  last_failed_at?: number
  duplicates?: NewsDuplicateGroup[]
}

export interface NewsDuplicateGroup {
  normalised_title: string
  ids: string[]
  services: string[]
  count: number
}

export interface NewsDedupResponse {
  groups: number
  rows_deleted: number
  rows_kept: number
}

// SuggestionsListResponse is the GET /dashboard/suggestions shape.
// Kept as an envelope so future top-level metadata (filter counts,
// last-generated timestamp) can be added without breaking clients.
export interface SuggestionsListResponse {
  items: StockSuggestion[]
}

// SuggestionsGenerateResponse mirrors GenerateResult on the server.
// The counters let the UI explain WHY no items came back ("LLM emitted
// 4 rows but all had non-KRX tickers") instead of a silent empty state.
export interface SuggestionsGenerateResponse {
  items: StockSuggestion[]
  llm_row_count: number
  rejected_non_krx_ticker: number
  rejected_unknown_action: number
  rejected_no_evidence: number
  /** Rows dropped because the ticker is already on the operator's
   *  watchlist. Prompt also tells the LLM to avoid these, so a non-
   *  zero value usually means the small model ignored the hint. */
  rejected_already_watched: number
  prompt_chars: number
  model?: string
  /** Per-market diagnostic snapshot when the dispatcher ran both KR
   *  and US. Empty when only one path fired (e.g. operator disabled
   *  the other market via settings). Same shape as the parent so the
   *  banner can render market-scoped counters. */
  per_market?: Record<'KR' | 'US', SuggestionsGenerateResponse | undefined>
  /** Raw ticker values the LLM emitted that failed validation.
   *  Surfaced in the UI so the operator sees "LLM said '삼성전자'"
   *  instead of guessing from logs. Capped at 5. */
  rejected_ticker_samples?: string[]
  rejected_action_samples?: string[]
  rejected_watched_samples?: string[]
}

export interface QuoteSnapshot {
  ticker: string
  /** 한글 종목명 (KIS hts_kor_isnm). Empty when KIS doesn't provide it
   *  (e.g. ETF/preferred shares occasionally). */
  name?: string
  price: number
  open: number
  high: number
  low: number
  prev_close: number
  volume: number
  change_pct: number
  fetched_at: number
  /** true when KIS failed this tick and the payload came from the
   *  persisted last-known quote (price_snapshots). UI dims the card
   *  + shows "마지막 N분 전" so the operator isn't misled. */
  stale?: boolean
}

export type NewsService =
  | 'kis_disclosure'
  | 'kis_market'
  | 'kis_overseas_breaking'
  | 'kis_overseas_news'
  | 'trend'

export interface NewsItem {
  id: string
  /** Backend service that delivered the item (trend-collector vs KIS). */
  service: NewsService
  /** Upstream publisher / origin — "naver_finance", "KIS · 공시", etc. */
  origin: string
  title: string
  body?: string
  url?: string
  ticker?: string
  category?: string
  severity?: string
  region?: string
  score?: number
  spike?: boolean
  published_at: number
}

export type NewsSentiment = 'bullish' | 'bearish' | 'neutral' | 'mixed'

// Market-wide news aggregate. `items` is scope=market only (거시/섹터/
// 전체시장) — ticker-specific news is filtered out upstream by the LLM
// scope classifier so the overview reflects macro mood, not a loud
// per-ticker story.
export interface MarketOverviewItem {
  id: string
  service: string
  origin?: string
  title: string
  url?: string
  summary_ko: string
  sentiment: NewsSentiment | ''
  sentiment_score: number
  published_at?: number
}

// One persisted row in the dashboard watchlist. source tracks
// provenance — "manual" for typed entries, "suggestion:<id>" when
// the auto-accept hook added it.
export interface WatchlistEntry {
  ticker: string
  label: string
  source: string
  added_at: number
}

export interface CalendarDayPlan {
  id: number
  kind: 'final' | 'draft'
  regime: string
  watchlist_count: number
}

export interface CalendarDayNews {
  summarised: number
  avg_sentiment: number
}

export interface CalendarDayTrades {
  crypto_buy: number
  crypto_sell: number
  crypto_notional_krw: string
  stock_buy: number
  stock_sell: number
  stock_notional_krw: number
  stock_realized_pnl_krw: number
  crypto_realized_pnl_krw: string
}

export interface CalendarDayNewsRow {
  id: string
  title: string
  summary_ko: string
  sentiment: string
  sentiment_score: number
  service: string
  origin?: string
  url?: string
  published_at: number
}

export interface CalendarDayCryptoOrder {
  uuid: string
  symbol: string
  side: 'bid' | 'ask' | string
  ord_type: string
  state: string
  price: string
  volume: string
  executed_volume: string
  submitted_by?: string
  submitted_at: number
}

export interface CalendarDayStockOrder {
  id: number
  ticker: string
  side: 'buy' | 'sell' | string
  qty: number
  price: number
  order_type: string
  status: string
  trade_mode: string
  kis_order_id?: string
  submitted_at: number
}

export interface CalendarDayDetailPayload {
  date: string
  news: CalendarDayNewsRow[]
  crypto_orders: CalendarDayCryptoOrder[]
  stock_orders: CalendarDayStockOrder[]
  stock_realized_pnl_krw: number
  crypto_realized_pnl_krw: string
}

export interface CalendarDay {
  date: string
  /** 0 = Sunday … 6 = Saturday. KST calendar. */
  weekday: number
  plan?: CalendarDayPlan
  news?: CalendarDayNews
  trades?: CalendarDayTrades
}

export interface CalendarPayload {
  month: string
  from: string
  to: string
  days: CalendarDay[]
}

/** One daily bucket of the sentiment sparkline — already aggregated
 *  server-side over the FULL 30-day window, not just the display slice.
 *  day_kst is a KST day index (unix-seconds/86400 after +9h shift) so
 *  the chart can numerically sort without date parsing. */
export interface FlowPoint {
  day_kst: number
  mean: number
  count: number
}

export interface MarketOverview {
  window_hours: number
  /** Scope the aggregate was computed over: 'market' (default KIS card)
   *  or 'crypto' (Bithumb-side 암호화폐 흐름 card). */
  scope?: 'market' | 'crypto' | 'ticker'
  item_count: number
  sentiment: NewsSentiment
  sentiment_score: number
  bullish: number
  bearish: number
  neutral: number
  mixed: number
  items: MarketOverviewItem[]
  /** Daily sentiment sparkline points, oldest→newest. Empty days are
   *  collapsed. Computed server-side across up to 2000 rows so the line
   *  reflects the whole window — not just the `items` display slice. */
  flow_points?: FlowPoint[]
  fetched_at: number
}

/** One LLM-summarised news item. Clickable: url opens the original. */
export interface NewsSummaryItem {
  id: string
  service: string
  origin?: string
  ticker?: string
  title: string
  url?: string
  summary_ko: string
  sentiment: NewsSentiment
  sentiment_score: number
  scope?: 'market' | 'ticker' | 'crypto'
  published_at?: number
}

/** Per-item summaries + a derived aggregate sentiment for the signal layer. */
export interface NewsSummary {
  id: number
  items: NewsSummaryItem[]
  sentiment: NewsSentiment
  sentiment_score: number
  input_count: number
  created_at: string
}

export interface SummaryStatus {
  inflight_started_at?: string
  inflight_digest?: string
  last_failure?: string
  last_failed_at?: string
}

export interface DashboardNews {
  summary?: NewsSummary
  /** true when no cached summary exists but the server has started an
   *  async LLM call; UI should poll for the result. */
  summary_pending: boolean
  /** Background job diagnostics (elapsed time + last failure). */
  summary_status: SummaryStatus
  /** Authoritative count of summarised rows in summary_window_hours,
   *  independent of summary.items.length (which is display-capped). */
  summary_total: number
  /** Hours the server scanned for summaries (widened past `hours`
   *  for cross-surface consistency with the queue panel). */
  summary_window_hours: number
  holdings: NewsItem[]
  market_wide: NewsItem[]
  /** Crypto-scoped rows (LLM scope=crypto OR keyword-matched). Rendered
   *  inside the Bithumb section so stock cards stay focused. */
  crypto: NewsItem[]
  held_tickers: string[]
  fetched_at: number
  /** Per-source upstream errors. Keys: "trend" | "kis". Empty when all
   *  sources succeeded (or were intentionally not configured). */
  source_errors?: Record<string, string>
  /** Per-source item counts — helps distinguish "upstream returned 0"
   *  from "upstream failed". Same keys as source_errors. */
  source_counts?: Record<string, number>
  /** Trend subscription filter currently applied server-side. */
  subscription?: {
    sources: string
    tags: string
  }
  /** Pagination over holdings + market_wide combined. Crypto has its
   *  own, non-paginated bucket — this only governs the stock-side
   *  feed the /news page paginates. Absent on pre-pagination builds. */
  pagination?: {
    offset: number
    limit: number
    total: number
    has_more: boolean
  }
}

export interface JournalEntry {
  ticker: string
  side: string
  kind: 'closed' | 'open' | 'scale_in' | 'scale_out'
  qty: number
  entry_at?: number
  exit_at?: number
  entry_price?: number
  exit_price?: number
  realised_pnl: number
  realised_pnl_pct: number
  entry_reason?: string
  exit_reason?: string
  plan_match: boolean
  decision_ids: number[]
}

export interface JournalSummary {
  date: string
  trade_mode: string
  realised_pnl: number
  total_trades: number
  wins: number
  losses: number
  open_carried: number
  guardrail_events: number
}

export interface DashboardRetrospective {
  summary: JournalSummary
  entries: JournalEntry[]
}

// Crypto bot trading journal wire types — bot's round-trip
// cycles + still-open positions + today-KST roll-up. All money
// fields arrive as decimal strings (preserves crypto precision);
// formatting is the UI's job.
export interface CryptoJournalDaily {
  date: string // YYYY-MM-DD KST
  realised_krw: string // NCF (net cash flow: sells − buys, incl. open-buy spend) — NOT P&L
  realised_closed_krw: string // Σ closed round-trip realized (aligns with trades_closed/wins/losses)
  trades_closed: number
  wins: number
  losses: number
  break_evens: number
  realised_all_time_krw: string
  trades_closed_all_time: number
  open_count: number
}

export interface CryptoJournalClosedCycle {
  symbol: string
  /** 한글 종목명 (KIS only). Empty for crypto rows. */
  name?: string
  opened_at: number // unix seconds
  closed_at: number
  bid_fills: number
  ask_fills: number
  entry_volume: string
  exit_volume: string
  entry_avg: string
  exit_avg: string
  entry_krw: string
  exit_krw: string
  realised_krw: string
  pnl_pct: number
}

export interface CryptoJournalOpenCycle {
  symbol: string
  /** 한글 종목명 (KIS only). Empty for crypto rows. */
  name?: string
  opened_at: number
  bid_fills: number
  ask_fills: number
  open_volume: string
  entry_avg: string
  entry_krw: string
  mark?: string             // live ticker (empty on fetch fail)
  unrealised_krw?: string   // (mark − entry_avg) × open_volume
  unrealised_pct?: number   // unrealised / entry_krw × 100
  /** "fills" = derived from filled orders; "broker" = synthetic row
   *  from broker holdings (fills sync hasn't caught up yet). */
  source?: 'fills' | 'broker'
  /** true when mark is known AND mark × open_volume < 5,000 KRW (Bithumb min notional) */
  is_dust: boolean
}

export interface CryptoJournal {
  trade_mode: string         // "live" | "paper"
  daily: CryptoJournalDaily
  open_cycles: CryptoJournalOpenCycle[]
  closed_cycles: CryptoJournalClosedCycle[]
  /** true when paper-mode done orders exist in DB (useful when viewing live mode) */
  paper_exists: boolean
}
